import React, { useState, useEffect } from 'react';
import Text from '../../Text/Text';
import UserValidator from '../../UserValidator/UserValidator';
import LabeledValue from '../../LabeledValue';
import StarRating from '../../StarRating/StarRating';
import { getConfig } from '../../../config';
import infoIcon from '../../../images/icons/info_icon.png';
import HelpTooltip from '../../HelpTooltip';
import { apiClient } from '../../../api/client';

interface InformationStepProps {
  initialData: {
    amount: string;
    platformFee: string;
    totalAmount: string;
    publicUserId: string;
    sellerId: string;
    itemId: string;
    userValid: boolean;
  };
  onSubmit: (data: InformationStepProps['initialData'] & {
    promotionCode?: string;
    originalPlatformFee?: string;
    appliedPromotion?: any;
  }) => void;
}

const InformationStep: React.FC<InformationStepProps> = ({ initialData, onSubmit }) => {
  // State initialization with initialData
  const [amount, setAmount] = useState(initialData.amount);
  const [platformFee, setPlatformFee] = useState(initialData.platformFee);
  const [totalAmount, setTotalAmount] = useState(initialData.totalAmount);
  const [userId, setUserId] = useState(initialData.publicUserId);
  const [sellerAuthId, setSellerAuthId] = useState(initialData.sellerId);
  const [itemId, setItemId] = useState(initialData.itemId);
  const [userValid, setUserValid] = useState(initialData.userValid);
  
  // Additional state for UI
  const [amountError, setAmountError] = useState('');
  const [userRating, setUserRating] = useState<number | undefined>(undefined);
  const [isValidatingUser, setIsValidatingUser] = useState(false);
  const [formError, setFormError] = useState('');

  // Promotion code state
  const [promotionCode, setPromotionCode] = useState('');
  const [promotionValid, setPromotionValid] = useState(false);
  const [promotionError, setPromotionError] = useState('');
  const [isValidatingPromotion, setIsValidatingPromotion] = useState(false);
  const [appliedPromotion, setAppliedPromotion] = useState<any>(null);
  const [originalPlatformFee, setOriginalPlatformFee] = useState('0.00');
  const [promotionValidationTimeout, setPromotionValidationTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isCouponExpanded, setIsCouponExpanded] = useState(false);

  // Icon components for collapsible coupon section
  const CouponIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M21 8V6H3v2c1.1 0 2 .9 2 2s-.9 2-2 2v2h18v-2c-1.1 0-2-.9-2-2s.9-2 2-2zm-3 8H6V8h12v8z"
        fill="currentColor"
      />
    </svg>
  );

  const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`coupon-toggle-chevron ${isExpanded ? 'expanded' : ''}`}
      aria-hidden="true"
    >
      <path
        d="M7 10l5 5 5-5z"
        fill="currentColor"
      />
    </svg>
  );

  // Toggle handler for collapsible coupon section
  const handleCouponToggle = () => {
    setIsCouponExpanded(prev => {
      const newState = !prev;
      if (newState) {
        // Focus input after animation completes
        setTimeout(() => {
          const input = document.querySelector('.promotion-code-input') as HTMLInputElement;
          input?.focus();
        }, 100);
      }
      return newState;
    });
  };

  // Calculate platform fee
  const calculatePlatformFee = (amount: string): number => {
    if (!amount || isNaN(parseFloat(amount))) return 0;
    
    const { payment } = getConfig();
    const percentageRate = payment?.platformFee?.percentage;
    const minimumFee = payment?.platformFee?.minimumFee;
    
    // REQUIRED: Configuration must be present - no fallback values
    if (percentageRate === undefined || percentageRate === 0 ||
        minimumFee === undefined || minimumFee === 0) {
      console.error('CRITICAL: Platform fee configuration is missing or invalid!', {
        percentageRate,
        minimumFee,
        config: payment,
        envVars: {
          percentage: process.env.REACT_APP_PLATFORM_FEE_PERCENTAGE,
          minimum: process.env.REACT_APP_PLATFORM_FEE_MINIMUM
        }
      });
      throw new Error(
        'Platform fee configuration is required. Please ensure REACT_APP_PLATFORM_FEE_PERCENTAGE ' +
        'and REACT_APP_PLATFORM_FEE_MINIMUM are set in environment variables.'
      );
    }
    
    const amountValue = parseFloat(amount);
    const percentageFee = amountValue * percentageRate;
    const finalFee = Math.max(percentageFee, minimumFee);
    
    console.debug('Platform fee calculation:', {
      amount: amountValue,
      percentageRate,
      percentageFee,
      minimumFee,
      finalFee,
      usingMinimum: finalFee === minimumFee
    });
    
    return finalFee;
  };

  // Promotion validation function
  const validatePromotion = async (code: string, amount: string) => {
    if (!code.trim()) {
      setPromotionValid(false);
      setAppliedPromotion(null);
      setPromotionError('');
      // Reset to original fee
      const originalFee = calculatePlatformFee(amount);
      setPlatformFee(originalFee.toFixed(2));
      setOriginalPlatformFee(originalFee.toFixed(2));
      return;
    }
    
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setPromotionError('Please enter a valid amount first');
      return;
    }

    setIsValidatingPromotion(true);
    setPromotionError('');
    
    let response;
    try {
      response = await apiClient.promotions.validatePromotion({
        code: code.trim().toUpperCase(),
        transactionAmount: parseFloat(amount)
      }) as any;
      
      if (response.isValid) {
        setPromotionValid(true);
        setAppliedPromotion(response.promotion);
        setPromotionError('');
        
        // Update platform fee with discount
        setPlatformFee(response.discountedFee.toFixed(2));
        setOriginalPlatformFee(response.originalFee.toFixed(2));
        
        // Update total amount
        const total = parseFloat(amount) + response.discountedFee;
        setTotalAmount(total.toFixed(2));
      } else {
        setPromotionValid(false);
        setPromotionError(response.errorMessage || 'Invalid promotion code');
        setAppliedPromotion(null);
        // Reset to original fee
        const originalFee = calculatePlatformFee(amount);
        setPlatformFee(originalFee.toFixed(2));
        setOriginalPlatformFee(originalFee.toFixed(2));
        const total = parseFloat(amount) + originalFee;
        setTotalAmount(total.toFixed(2));
      }
    } catch (error) {
      console.log('Error validating promotion:', error);
      setPromotionValid(false);
      setPromotionError('Error validating promotion code');
      setAppliedPromotion(null);
      // Reset to original fee
      const originalFee = calculatePlatformFee(amount);
      setPlatformFee(originalFee.toFixed(2));
      setOriginalPlatformFee(originalFee.toFixed(2));
      const total = parseFloat(amount) + originalFee;
      setTotalAmount(total.toFixed(2));
    } finally {
      setIsValidatingPromotion(false);
    }
  };

  // Update fee and total when amount changes
  useEffect(() => {
    const fee = calculatePlatformFee(amount);
    
    // If no promotion is applied, use standard fee calculation
    if (!appliedPromotion) {
      setPlatformFee(fee.toFixed(2));
      setOriginalPlatformFee(fee.toFixed(2));
      
      if (amount && !isNaN(parseFloat(amount))) {
        const total = parseFloat(amount) + fee;
        setTotalAmount(total.toFixed(2));
      } else {
        setTotalAmount('0.00');
      }
    } else {
      // If promotion is applied, re-validate it with the new amount
      if (promotionCode && amount) {
        validatePromotion(promotionCode, amount);
      }
    }
  }, [amount]);

  // Debounced promotion validation effect
  useEffect(() => {
    // Clear any existing timeout
    if (promotionValidationTimeout) {
      clearTimeout(promotionValidationTimeout);
    }

    // If promotion code is empty, reset immediately
    if (!promotionCode.trim()) {
      setPromotionValid(false);
      setAppliedPromotion(null);
      setPromotionError('');
      // Reset to original fee
      const originalFee = calculatePlatformFee(amount);
      setPlatformFee(originalFee.toFixed(2));
      setOriginalPlatformFee(originalFee.toFixed(2));
      if (amount && !isNaN(parseFloat(amount))) {
        const total = parseFloat(amount) + originalFee;
        setTotalAmount(total.toFixed(2));
      }
      return;
    }

    // Set up debounced validation
    const timeoutId = setTimeout(() => {
      if (promotionCode && amount) {
        validatePromotion(promotionCode, amount);
      }
    }, 500); // 500ms debounce delay

    setPromotionValidationTimeout(timeoutId);

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [promotionCode, amount]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check for required fields
    if (!amount || !userId || !itemId) {
      setFormError('Please fill in all required fields');
      return;
    }
    
    // Validate amount is a positive number
    const amountValue = parseFloat(amount);
    console.log('Validating amount:', amountValue);
    if (isNaN(amountValue) || amountValue <= 0) {
      console.log('Invalid amount:', amount);
      setFormError('Please enter a valid amount greater than $0');
      return;
    }
    
    // Validate amount doesn't exceed maximum
    if (amountValue > 1000) {
      console.log('Amount exceeds maximum:', amount);
      setFormError('Amount cannot exceed $1000');
      return;
    }
    
    if (!userValid) {
      setFormError('Please enter a valid VeriPay ID');
      return;
    }
    
    onSubmit({
      amount,
      platformFee,
      totalAmount,
      publicUserId: userId,
      sellerId: sellerAuthId,
      itemId,
      userValid,
      promotionCode: promotionCode || undefined,
      originalPlatformFee: originalPlatformFee || undefined,
      appliedPromotion: appliedPromotion || undefined
    });
  };

  // Other handlers
  const handleUserChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUserId(newValue);
    setUserValid(false);
    if (newValue) {
      setIsValidatingUser(true);
    }
  };

  const validateAmount = (input: HTMLInputElement) => {
    if (!input.value) {
      setAmountError('Amount is required');
      return false;
    }
    
    if (!input.validity.valid) {
      setAmountError('Invalid amount - max $1000');
      return false;
    }

    setAmountError('');
    return true;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^\d*(\.\d{0,2})?$/; // Allow empty string and partial numbers
    
    // Allow format-valid values (including empty string for complete clearing)
    if (regex.test(value)) {
      setAmount(value);
      // Only validate for business rules if there's a value
      if (value) {
        validateAmount(e.target);
      } else {
        // Clear error when field is empty (allows complete clearing)
        setAmountError('');
      }
    } else {
      // Only revert if the format is completely invalid
      e.target.value = amount;
    }
  };

  const handleUserValidation = (isValid: boolean, publicUserId?: string, sellerId?: string, rating?: number) => {
    setUserValid(isValid);
    if (sellerId) {
      setSellerAuthId(sellerId);
    }
    setUserRating(rating);
    setIsValidatingUser(false);
    setIsValidatingUser(false);
  };

  // Check for retry data in session storage
  useEffect(() => {
    // Check if we have retry data in session storage
    const retryData = sessionStorage.getItem('veripay_payment_retry');
    if (retryData) {
      try {
        const data = JSON.parse(retryData);
        if (data.amount) setAmount(data.amount.toString());
        if (data.sellerId || data.userId) setUserId(data.sellerId || data.userId);
        if (data.itemId) setItemId(data.itemId);
        
        // Clear the retry data
        sessionStorage.removeItem('veripay_payment_retry');
        
        // If we have a user ID, validate it
        if (data.sellerId || data.userId) {
          setIsValidatingUser(true);
        }
      } catch (error) {
        console.error('Error parsing retry data:', error);
      }
    }
    
    // Auto-expand coupon section if promotion code exists
    if (promotionCode && promotionCode.trim()) {
      setIsCouponExpanded(true);
    }
  }, []);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (promotionValidationTimeout) {
        clearTimeout(promotionValidationTimeout);
      }
    };
  }, [promotionValidationTimeout]);

  return (
    <div className="information-step">
      <h2>Payment Details</h2>
      
      <form onSubmit={handleSubmit}>
        {/* Amount Input */}
        <div className="form-group">
          <Text className="text-body">Amount to Pay</Text>
          <div className="input-wrapper">
            <div className="input-prefix">$</div>
            <input
              type="number"
              className={`input-field-amount ${amountError ? 'input-error' : ''}`}
              placeholder="Enter value (max $1000)"
              value={amount}
              onInput={handleAmountChange}
              step="1"
              max="1000"
            />
          </div>
          {amountError && (
            <Text className="text-caption text-error">
              {amountError}
            </Text>
          )}
        </div>

        {/* Collapsible Coupon Code Section */}
        <div className="coupon-section">
          {/* Toggle Button - Collapsed State */}
          <button
            type="button"
            className="coupon-toggle-button"
            onClick={handleCouponToggle}
            aria-expanded={isCouponExpanded}
            aria-controls="coupon-input-container"
          >
            <CouponIcon />
            <Text className="text-body coupon-toggle-text">
              Have a promotion code?
            </Text>
            <ChevronIcon isExpanded={isCouponExpanded} />
          </button>

          {/* Collapsible Container - Expanded State */}
          <div
            id="coupon-input-container"
            className={`coupon-collapsible-container ${isCouponExpanded ? 'expanded' : ''}`}
            aria-hidden={!isCouponExpanded}
          >
            <div className="form-group coupon-input-group">
              <Text className="text-body">Promotion Code</Text>
              <input
                type="text"
                className={`input-field promotion-code-input ${
                  promotionError ? 'input-error' : promotionValid ? 'input-success' : ''
                }`}
                placeholder="Enter promotion code"
                value={promotionCode}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setPromotionCode(value);
                  // Clear previous validation state when user types
                  if (value !== promotionCode) {
                    setPromotionValid(false);
                    setPromotionError('');
                    setAppliedPromotion(null);
                  }
                }}
                maxLength={50}
                aria-label="Promotion code"
                aria-describedby={
                  promotionError ? 'promotion-error' :
                  promotionValid ? 'promotion-success' :
                  undefined
                }
              />
              
              {/* Validation States */}
              {isValidatingPromotion && (
                <Text className="text-caption text-info">
                  Validating promotion...
                </Text>
              )}
              {promotionError && (
                <span
                  id="promotion-error"
                  className="text text-caption text-error"
                  role="alert"
                >
                  {promotionError}
                </span>
              )}
              {promotionValid && appliedPromotion && (
                <span
                  id="promotion-success"
                  className="text text-caption text-success"
                  role="status"
                >
                  ✓ {appliedPromotion.name} applied!
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Platform Fee and Total */}
        <div className="form-group">
          {appliedPromotion && (
            <LabeledValue
              label="Original Platform Fee"
              value={`$${originalPlatformFee}`}
              className="text-body text-strikethrough"
              rightAligned={true}
            />
          )}
          <LabeledValue
            label={appliedPromotion ? "Discounted Platform Fee" : "Platform Fee"}
            value={`$${platformFee}`}
            className={`text-body ${appliedPromotion ? 'text-success' : ''}`}
            rightAligned={true}
          />
          {appliedPromotion && (
            <LabeledValue
              label="Promotion Savings"
              value={`-$${(parseFloat(originalPlatformFee) - parseFloat(platformFee)).toFixed(2)}`}
              className="text-body text-success"
              rightAligned={true}
            />
          )}
          <LabeledValue
            label="Total Amount"
            value={`$${totalAmount}`}
            className="text-body text-bold"
            rightAligned={true}
          />
          
          <div className="info-panel">
            <div className="info-header">
              <img 
                src={infoIcon}
                alt="Information" 
                className="info-icon" 
              />
              <Text className="info-text">
                This small fee helps us maintain the VeriPay platform, ensuring secure transactions and excellent customer support for all users.
              </Text>
            </div>
          </div>
        </div>

        {/* User ID Input */}
        <div className="form-group">
          <Text className="text-body">Sellers VeriPay ID</Text>
          <input
            type="text"
            className={`input-field ${!userValid && userId ? 'input-error' : ''}`}
            placeholder="Enter user ID"
            value={userId}
            onChange={handleUserChange}
          />
          <UserValidator
            userId={userId}
            onValidationChange={handleUserValidation}
          />
          {userValid && (
            <div className="seller-rating">
              <Text className="text-caption">This Sellers Rating:</Text>
              <HelpTooltip
                content="The seller's rating is based on previous transactions and reflects their reliability and quality of service. 1 star indicates poor service, while 5 stars indicate excellent service."
                position="top"
                ariaLabel="Seller rating information"
              />
              <StarRating
                rating={userRating || 0}
                className={!userRating ? 'rating-placeholder' : ''}
              />
            </div>
          )}
        </div>

        {/* Item Description */}
        <div className="form-group">
          <Text className="text-body">Item Description</Text>
          <input
            type="text"
            className="input-field"
            placeholder="Enter item description"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            maxLength={40}
          />
        </div>

        {/* Submit Button */}
        <div className="form-group">
            <button
            type="submit"
            className="submit-button"
            disabled={
              !userValid ||
              !amount ||
              !itemId ||
              itemId.trim().length === 0 ||
              isValidatingUser ||
              isNaN(parseFloat(amount)) ||
              parseFloat(amount) <= 0 ||
              parseFloat(amount) > 1000
            }
            >
            <Text className="text-body text-white">
              {`Continue to Payment: $${totalAmount}`}
            </Text>
            </button>
          
          {formError && (
            <Text className="text-caption text-error">
              {formError}
            </Text>
          )}
        </div>
      </form>
    </div>
  );
};

export default InformationStep;